import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import axios from "axios";
import { useUniboxColors } from "../../useUniboxColors";

const API = process.env.REACT_APP_API_URL;

const PERIODS = [
  { days: 7, label: "7 días" },
  { days: 30, label: "30 días" },
  { days: 90, label: "90 días" },
  { days: 365, label: "1 año" },
];

const DOMAIN_PRESETS = [
  { id: "all", mailbox: "", counterparty: "", label: "Todas mis cuentas" },
  {
    id: "stage",
    mailbox: "stagedesign.es",
    counterparty: "",
    label: "stagedesign.es",
  },
  {
    id: "cross",
    mailbox: "stagedesign.es",
    counterparty: "nomanslabel.es",
    label: "stagedesign ↔ nomanslabel",
  },
  {
    id: "noman",
    mailbox: "nomanslabel.es",
    counterparty: "",
    label: "nomanslabel.es",
  },
];

function formatNum(n) {
  return Number(n || 0).toLocaleString("es-ES");
}

function readCrmIsAdmin() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return false;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return !!payload.isAdmin;
  } catch {
    return false;
  }
}

export default function EmailStats() {
  const c = useUniboxColors();
  const isAdmin = useMemo(() => readCrmIsAdmin(), []);
  const [days, setDays] = useState(30);
  const [preset, setPreset] = useState("stage");
  const [scope, setScope] = useState(isAdmin ? "org" : "user");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const presetCfg = useMemo(
    () => DOMAIN_PRESETS.find((p) => p.id === preset) || DOMAIN_PRESETS[0],
    [preset]
  );

  const effectiveScope = useMemo(() => {
    if (!isAdmin || scope !== "org") return "user";
    if (!presetCfg.mailbox) return "user";
    return "org";
  }, [isAdmin, scope, presetCfg.mailbox]);

  const loadStats = useCallback(async () => {
    if (!API) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/emails/stats`, {
        params: {
          days,
          scope: effectiveScope,
          mailboxDomain: presetCfg.mailbox || undefined,
          counterpartyDomain: presetCfg.counterparty || undefined,
        },
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.error ||
        "No se pudieron cargar las estadísticas.";
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, presetCfg, effectiveScope]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: c.page,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: `1px solid ${c.border}`,
          bgcolor: c.card,
          background: `linear-gradient(135deg, ${c.accentSoft} 0%, ${c.card} 55%)`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <BarChartOutlinedIcon sx={{ color: c.accent, fontSize: 28 }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: -0.3 }}>
              Estadísticas de correo
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: c.textMuted }}>
              Volumen por buzón y tráfico entre dominios (datos sincronizados en MATRIXBOX)
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          sx={{ mt: 1.75, flexWrap: "wrap", gap: 1 }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, v) => v && setDays(v)}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12,
                px: 1.5,
              },
            }}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.days} value={p.days}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
            {DOMAIN_PRESETS.map((p) => (
              <Chip
                key={p.id}
                label={p.label}
                size="small"
                onClick={() => {
                  setPreset(p.id);
                  if (!p.mailbox && scope === "org") setScope("user");
                }}
                sx={{
                  fontWeight: 700,
                  bgcolor: preset === p.id ? c.accent : c.panel,
                  color: preset === p.id ? "#fff" : c.textPrimary,
                  border: `1px solid ${preset === p.id ? c.accent : c.border}`,
                }}
              />
            ))}
          </Stack>

          {isAdmin ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={effectiveScope}
              onChange={(_, v) => v && setScope(v)}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  px: 1.5,
                },
              }}
            >
              <ToggleButton value="org" disabled={!presetCfg.mailbox}>
                Empresa (todos @stagedesign…)
              </ToggleButton>
              <ToggleButton value="user">Solo mis cuentas</ToggleButton>
            </ToggleButtonGroup>
          ) : null}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, md: 3 }, py: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: c.accent }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Stack spacing={2}>
            {data?.note ? (
              <Alert severity="info" sx={{ fontSize: 12.5 }}>
                {data.note}
              </Alert>
            ) : null}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 1.25,
              }}
            >
              {[
                { label: "Recibidos", value: data?.totals?.received },
                { label: "Enviados", value: data?.totals?.sent },
                { label: "Total", value: data?.totals?.total },
                {
                  label: "Buzones",
                  value: data?.mailboxes?.length,
                },
              ].map((card) => (
                <Paper
                  key={card.label}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: c.radiusOuter,
                    border: `1px solid ${c.border}`,
                    bgcolor: c.card,
                  }}
                >
                  <Typography sx={{ fontSize: 11, color: c.textDim, fontWeight: 700 }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: c.textPrimary }}>
                    {formatNum(card.value)}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {data?.crossDomain ? (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: c.radiusOuter,
                  border: `1px solid ${c.accentBorder}`,
                  bgcolor: c.accentSoft,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 1 }}>
                  Tráfico @{data.crossDomain.mailboxDomain} ↔ @
                  {data.crossDomain.counterpartyDomain}
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Typography sx={{ fontSize: 13 }}>
                    Recibidos desde nomanslabel:{" "}
                    <strong>{formatNum(data.crossDomain.receivedFromCounterparty)}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Enviados hacia nomanslabel:{" "}
                    <strong>{formatNum(data.crossDomain.sentToCounterparty)}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Total intercambio: <strong>{formatNum(data.crossDomain.total)}</strong>
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
              Por buzón / persona
              {data?.scope === "org" ? " (toda la empresa)" : ""}
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: `1px solid ${c.border}`, borderRadius: c.radiusOuter }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: c.panel }}>
                    <TableCell sx={{ fontWeight: 800 }}>Cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Dominio</TableCell>
                    {data?.scope === "org" ? (
                      <TableCell sx={{ fontWeight: 800 }}>Usuario CRM</TableCell>
                    ) : null}
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      Recibidos
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      Enviados
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.mailboxes || []).length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={data?.scope === "org" ? 6 : 5}
                        sx={{ color: c.textMuted }}
                      >
                        Sin datos para este filtro y periodo.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.mailboxes.map((row) => (
                      <TableRow key={row.accountEmail} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.accountEmail}</TableCell>
                        <TableCell>{row.domain}</TableCell>
                        {data?.scope === "org" ? (
                          <TableCell sx={{ color: c.textMuted, fontSize: 12 }}>
                            #{row.connectedByUserId ?? "—"}
                          </TableCell>
                        ) : null}
                        <TableCell align="right">{formatNum(row.received)}</TableCell>
                        <TableCell align="right">{formatNum(row.sent)}</TableCell>
                        <TableCell align="right">{formatNum(row.total)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {(data?.topSendersToMailboxes || []).length > 0 ? (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: 15, mt: 1 }}>
                  Principales remitentes (recibidos)
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: `1px solid ${c.border}`, borderRadius: c.radiusOuter }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: c.panel }}>
                        <TableCell sx={{ fontWeight: 800 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Dominio</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                          Mensajes
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.topSendersToMailboxes.map((row) => (
                        <TableRow key={row.email} hover>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.domain}</TableCell>
                          <TableCell align="right">{formatNum(row.count)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
